// Trade Room v8 — dual-skin (Simulator / Terminal), client-side verdict,
// /players-style compact header, derived SP/RP roles.
import React, {
  useState, useEffect, useMemo, useRef, useContext, createContext, useCallback,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Grid, Chip, Stack, Avatar, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, InputAdornment, Autocomplete, CircularProgress, Skeleton,
  Tabs, Tab, ToggleButton, ToggleButtonGroup, Tooltip, Collapse,
  ButtonBase, Divider, useTheme, useMediaQuery, alpha,
} from '@mui/material';
import {
  Search, Close, SwapHoriz, CompareArrows, ExpandMore, ContentCopy,
} from '@mui/icons-material';
import {
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, Radar, Legend,
} from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';
import { getTeamLogoUrl } from '../utils/teamLogos';
import { tradeApi } from '../services/apiService';

// ─── Formatters & data helpers ────────────────────────────────────────────────
const headshotUrl = (mlbId, size = 120) =>
  mlbId
    ? `https://img.mlbstatic.com/mlb-photos/image/upload/d_people:generic:headshot:67:current.png/w_${size},q_auto:best/v1/people/${mlbId}/headshot/67/current`
    : null;

const fmtDollar = (v) => {
  if (v == null || Number.isNaN(v)) return '—';
  const abs = Math.abs(v);
  const s = abs >= 1e6 ? `$${(abs / 1e6).toFixed(1)}M` : abs >= 1e3 ? `$${(abs / 1e3).toFixed(0)}K` : `$${abs.toFixed(0)}`;
  return v < 0 ? `−${s}` : s;
};

const fmtSurplus = (v) => {
  if (v == null || Number.isNaN(v)) return '—';
  const abs = Math.abs(v);
  const s = abs >= 1e6 ? `$${(abs / 1e6).toFixed(1)}M` : `$${(abs / 1e3).toFixed(0)}K`;
  return v < 0 ? `−${s}` : `+${s}`;
};

const fmtStat = (v, d = 1) => (v == null || Number.isNaN(v) ? '—' : Number(v).toFixed(d));

const playerWAR = (p) => p?.stats?.war ?? p?.metrics?.war ?? 0;
const playerSurplus = (p) => p?.surplusValue ?? p?.futureValue?.surplus?.totalContractSurplus ?? null;
const playerControl = (p) => p?.controlYears ?? p?.salary?.controlYears ?? null;
const playerPos = (p) => p?.stats?.position || p?.metrics?.position || '?';
const playerSalary = (p) => p?.salary?.salary ?? null;
const playerCVR = (p) => p?.stats?.cvr ?? p?.stats?.universalCVR ?? null;

const PITCHER_POS = ['P', 'SP', 'RP', 'CL', 'TWP'];

// Derive SP/RP from actual usage — leaders payload labels many pitchers 'P'.
const playerRole = (p) => {
  const raw = playerPos(p);
  const pit = p?.stats?.pitching;
  const ip = parseFloat(pit?.inningsPitched || 0);
  if (pit && ip > 0 && (PITCHER_POS.includes(raw) || raw === '?')) {
    const gs = pit.gamesStarted || 0;
    const g = pit.gamesPlayed || pit.games || 0;
    if (g > 0) return gs / g >= 0.5 ? 'SP' : 'RP';
  }
  return raw;
};

const isPitcher = (p) => PITCHER_POS.includes(playerRole(p)) || PITCHER_POS.includes(playerPos(p));

const TIERS = [
  { min: 85, label: 'MVP Caliber',   color: '#FFD700' },
  { min: 75, label: 'All-Star',      color: '#FF6B35' },
  { min: 65, label: 'Quality',       color: '#4CAF50' },
  { min: 55, label: 'Solid Starter', color: '#2196F3' },
  { min: 45, label: 'Average',       color: '#9E9E9E' },
  { min: 35, label: 'Below Avg',     color: '#795548' },
  { min: 25, label: 'Fringe',        color: '#FF5722' },
  { min: -Infinity, label: 'Replacement', color: '#F44336' },
];
const tierFor = (p) => {
  const tv = p?.tradeValue || 0;
  return TIERS.find((t) => tv >= t.min);
};

const MARKET_POSITIONS = ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH', 'SP', 'RP'];

// ─── Skin system ─────────────────────────────────────────────────────────────
const SkinContext = createContext(null);
const useSkin = () => useContext(SkinContext);

function makeSkin(theme, key) {
  if (key === 'terminal') {
    return {
      key, isTerminal: true,
      bg0: '#0A0D13', bg1: '#10141C', bg2: '#161C28',
      border: '#1F2633',
      text: '#E8ECF4', sub: '#8A93A6', faint: '#5A6376',
      accent: '#F5A623', info: '#4FA3FF',
      pos: '#30D158', neg: '#FF5F52', warn: '#F5A623',
      mono: '"SF Mono","JetBrains Mono","Roboto Mono",monospace',
      radius: 1.5,
      card: { bgcolor: '#10141C', border: '1px solid #1F2633', borderRadius: 1.5 },
      hoverBg: 'rgba(245,166,35,0.06)',
    };
  }
  const dk = theme.palette.mode === 'dark';
  return {
    key, isTerminal: false,
    bg0: 'transparent',
    bg1: theme.palette.background.paper,
    bg2: alpha(theme.palette.text.primary, 0.04),
    border: theme.palette.divider,
    text: theme.palette.text.primary,
    sub: theme.palette.text.secondary,
    faint: theme.palette.text.disabled,
    accent: theme.palette.primary.main,
    info: theme.palette.info.main,
    pos: theme.palette.success.main,
    neg: theme.palette.error.main,
    warn: theme.palette.warning.main,
    mono: 'monospace',
    radius: 3,
    card: {
      bgcolor: theme.palette.background.paper,
      border: '1px solid ' + theme.palette.divider,
      borderRadius: 3,
    },
    hoverBg: alpha(theme.palette.primary.main, dk ? 0.08 : 0.05),
  };
}

const surplusColor = (v, sk) => {
  if (v == null) return sk.faint;
  if (v >= 20e6) return sk.pos;
  if (v >= 0) return sk.text;
  return sk.neg;
};
const tvColor = (tv, sk) => (tv >= 65 ? sk.pos : tv >= 45 ? sk.text : tv >= 0 ? sk.sub : sk.neg);

// ─── Shared data (leaders) ────────────────────────────────────────────────────
const TradeDataContext = createContext({ players: [], loading: true });
const useTradeData = () => useContext(TradeDataContext);

function TradeDataProvider({ children }) {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    tradeApi
      .getTradeLeaders({ limit: 500, minGames: 5, category: 'all', mode: 'rolling' })
      .then((res) => {
        if (!alive) return;
        setPlayers(res?.leaders || res?.players || []);
      })
      .catch(() => alive && setPlayers([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const value = useMemo(() => ({ players, loading }), [players, loading]);
  return <TradeDataContext.Provider value={value}>{children}</TradeDataContext.Provider>;
}

// ─── Player face (headshot + team badge) ─────────────────────────────────────
function PlayerFace({ player, size = 40 }) {
  const src = headshotUrl(player?.mlbId, Math.max(size * 3, 96));
  const badge = Math.max(14, Math.round(size * 0.42));
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <Avatar
        src={src || undefined}
        sx={{ width: size, height: size, fontSize: size * 0.4, bgcolor: 'action.selected' }}
      >
        {(player?.name || '?').charAt(0)}
      </Avatar>
      {player?.team && (
        <Avatar
          src={getTeamLogoUrl(player.team)}
          sx={{
            position: 'absolute', bottom: -2, right: -3,
            width: badge, height: badge,
            border: '1.5px solid rgba(0,0,0,0.15)', bgcolor: 'white',
          }}
          imgProps={{ style: { objectFit: 'contain' } }}
        />
      )}
    </Box>
  );
}

// ─── Player search (hybrid: local leaders + API) ─────────────────────────────
function PlayerSearch({ onSelect, excludeIds = [], placeholder = 'Add a player…' }) {
  const sk = useSkin();
  const { players } = useTradeData();
  const [input, setInput] = useState('');
  const [apiOptions, setApiOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const queryRef = useRef('');

  useEffect(() => {
    const q = input.trim();
    queryRef.current = q;
    if (q.length < 2) { setApiOptions([]); setSearching(false); return; }
    clearTimeout(debounceRef.current);
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await tradeApi.searchPlayers(q, { limit: 15, mode: 'rolling' });
        if (queryRef.current !== q) return;
        setApiOptions(res?.players || res?.results || []);
      } catch { /* local results still shown */ }
      finally { if (queryRef.current === q) setSearching(false); }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [input]);

  const options = useMemo(() => {
    const q = input.trim().toLowerCase();
    if (q.length < 2) return [];
    const local = players.filter((p) => (p.name || '').toLowerCase().includes(q));
    const seen = new Set(local.map((p) => p.playerId));
    const merged = [...local];
    for (const p of apiOptions) {
      if (!seen.has(p.playerId)) { merged.push(p); seen.add(p.playerId); }
    }
    return merged.filter((p) => !excludeIds.includes(p.playerId)).slice(0, 12);
  }, [input, players, apiOptions, excludeIds]);

  return (
    <Autocomplete
      size="small"
      options={options}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      value={null}
      onChange={(_, v) => { if (v) { onSelect(v); setInput(''); setApiOptions([]); } }}
      getOptionLabel={(o) => o?.name || ''}
      isOptionEqualToValue={(o, v) => o.playerId === v.playerId}
      filterOptions={(x) => x}
      noOptionsText={input.trim().length < 2 ? 'Type a player name…' : searching ? 'Searching…' : 'No players found'}
      loading={searching}
      blurOnSelect
      renderOption={(props, opt) => {
        const surplus = playerSurplus(opt);
        return (
          <Box component="li" {...props} key={opt.playerId} sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
            <PlayerFace player={opt} size={34} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>{opt.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {opt.team} · {playerRole(opt)}{opt.age ? ` · ${opt.age}` : ''}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontWeight: 700 }}>
                {fmtSurplus(surplus)}
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: '0.62rem' }}>
                CTS {fmtStat(opt.tradeValue, 0)}
              </Typography>
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 16, color: sk.isTerminal ? sk.faint : 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {searching ? <CircularProgress size={14} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
            sx: sk.isTerminal ? {
              color: sk.text, fontSize: '0.85rem',
              '& fieldset': { borderColor: sk.border + ' !important' },
              '&:hover fieldset': { borderColor: sk.faint + ' !important' },
              '&.Mui-focused fieldset': { borderColor: sk.accent + ' !important' },
            } : { fontSize: '0.875rem' },
          }}
          inputProps={{
            ...params.inputProps,
            style: sk.isTerminal ? { color: sk.text } : undefined,
          }}
        />
      )}
    />
  );
}

// ─── Asset card (player in a trade side) ─────────────────────────────────────
function AssetCard({ player, onRemove }) {
  const sk = useSkin();
  const tier = tierFor(player);
  const surplus = playerSurplus(player);
  const control = playerControl(player);
  const war = playerWAR(player);
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.25,
      px: 1.5, py: 1,
      borderRadius: sk.isTerminal ? 1 : 2,
      bgcolor: sk.isTerminal ? sk.bg2 : sk.bg2,
      borderLeft: `3px solid ${tier.color}`,
      border: sk.isTerminal ? `1px solid ${sk.border}` : undefined,
      borderLeftWidth: 3, borderLeftColor: tier.color, borderLeftStyle: 'solid',
    }}>
      <PlayerFace player={player} size={40} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography variant="body2" fontWeight={700} noWrap sx={{ color: sk.text }}>
            {player.name}
          </Typography>
          <Chip
            label={tier.label}
            size="small"
            sx={{
              height: 16, fontSize: '0.58rem', fontWeight: 700,
              bgcolor: alpha(tier.color, 0.15), color: tier.color,
              '& .MuiChip-label': { px: 0.6 },
            }}
          />
        </Box>
        <Typography variant="caption" sx={{ color: sk.sub, display: 'block', lineHeight: 1.4 }}>
          {player.team} · {playerRole(player)}{player.age ? ` · ${player.age}` : ''} · {fmtStat(war)} WAR
        </Typography>
        <Typography variant="caption" sx={{ color: sk.faint, fontFamily: sk.mono, fontSize: '0.65rem' }}>
          {fmtDollar(playerSalary(player))}/yr{control != null ? ` · ${control} yr${control === 1 ? '' : 's'} control` : ''}
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
        <Typography variant="body2" sx={{ fontFamily: sk.mono, fontWeight: 800, color: surplusColor(surplus, sk), fontSize: '0.85rem' }}>
          {fmtSurplus(surplus)}
        </Typography>
        <Typography variant="caption" sx={{ fontFamily: sk.mono, color: tvColor(player.tradeValue || 0, sk), fontSize: '0.62rem' }}>
          CTS {fmtStat(player.tradeValue, 0)}
        </Typography>
      </Box>
      <IconButton size="small" onClick={onRemove} sx={{ color: sk.faint, '&:hover': { color: sk.neg } }}>
        <Close sx={{ fontSize: 15 }} />
      </IconButton>
    </Box>
  );
}

// ─── Trade side panel ─────────────────────────────────────────────────────────
function TradeSidePanel({ label, players, setPlayers, excludeIds }) {
  const sk = useSkin();
  const totSurplus = players.reduce((s, p) => s + (playerSurplus(p) ?? 0), 0);
  const totWar = players.reduce((s, p) => s + playerWAR(p), 0);
  const totSalary = players.reduce((s, p) => s + (playerSalary(p) ?? 0), 0);

  return (
    <Box sx={{ ...sk.card, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{
        px: 2, py: 1.25, borderBottom: `1px solid ${sk.border}`,
        display: 'flex', alignItems: 'baseline', gap: 1,
      }}>
        <Typography variant="subtitle2" fontWeight={800} sx={{ color: sk.text, textTransform: sk.isTerminal ? 'uppercase' : 'none', letterSpacing: sk.isTerminal ? 0.8 : 0, fontSize: sk.isTerminal ? '0.72rem' : undefined }}>
          {label}
        </Typography>
        <Typography variant="caption" sx={{ color: sk.faint }}>
          {players.length} player{players.length === 1 ? '' : 's'}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {players.length > 0 && (
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" sx={{ fontFamily: sk.mono, fontWeight: 800, color: surplusColor(totSurplus, sk), lineHeight: 1.2 }}>
              {fmtSurplus(totSurplus)}
            </Typography>
            <Typography variant="caption" sx={{ color: sk.faint, fontSize: '0.62rem' }}>
              {fmtStat(totWar)} WAR · {fmtDollar(totSalary)}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
        <PlayerSearch
          onSelect={(p) => setPlayers((prev) => [...prev, p])}
          excludeIds={excludeIds}
        />
        <AnimatePresence initial={false}>
          {players.map((p) => (
            <motion.div
              key={p.playerId}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.15 }}
            >
              <AssetCard
                player={p}
                onRemove={() => setPlayers((prev) => prev.filter((x) => x.playerId !== p.playerId))}
              />
            </motion.div>
          ))}
        </AnimatePresence>
        {players.length === 0 && (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: sk.faint }}>
              Search above to add players
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ─── Verdict (computed client-side from the exact card numbers) ──────────────
function computeVerdict(sideA, sideB) {
  const tot = (ps) => ps.reduce(
    (acc, p) => {
      acc.surplus += playerSurplus(p) ?? 0;
      acc.war += playerWAR(p);
      acc.cts += p.tradeValue || 0;
      acc.control += playerControl(p) ?? 0;
      acc.salary += playerSalary(p) ?? 0;
      return acc;
    },
    { surplus: 0, war: 0, cts: 0, control: 0, salary: 0 }
  );
  const a = tot(sideA);
  const b = tot(sideB);
  const diff = a.surplus - b.surplus;
  const denom = Math.max(Math.abs(a.surplus), Math.abs(b.surplus), 20e6);
  const pct = Math.abs(diff) / denom;
  const winner = pct < 0.075 ? null : diff > 0 ? 'A' : 'B';
  const band = pct < 0.075 ? 'FAIR' : pct < 0.2 ? 'SLIGHT' : pct < 0.45 ? 'CLEAR' : 'LOPSIDED';
  return { a, b, diff, pct, winner, band };
}

const BAND_META = {
  FAIR:     { label: 'Fair trade' },
  SLIGHT:   { label: 'Slight edge' },
  CLEAR:    { label: 'Clear win' },
  LOPSIDED: { label: 'Lopsided' },
};

function bandColor(band, sk) {
  switch (band) {
    case 'FAIR': return sk.pos;
    case 'SLIGHT': return sk.info;
    case 'CLEAR': return sk.warn;
    default: return sk.neg;
  }
}

function FairnessBar({ verdict }) {
  const sk = useSkin();
  const { a, b } = verdict;
  const total = Math.abs(a.surplus) + Math.abs(b.surplus);
  const shift = total > 0 ? ((a.surplus - b.surplus) / (2 * Math.max(total, 40e6))) * 100 : 0;
  const pos = Math.min(88, Math.max(12, 50 + shift * 2));
  const color = bandColor(verdict.band, sk);
  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" fontWeight={verdict.winner === 'A' ? 800 : 500} sx={{ color: verdict.winner === 'A' ? color : sk.sub }}>
          Side A {fmtSurplus(a.surplus)}
        </Typography>
        <Typography variant="caption" fontWeight={verdict.winner === 'B' ? 800 : 500} sx={{ color: verdict.winner === 'B' ? color : sk.sub }}>
          Side B {fmtSurplus(b.surplus)}
        </Typography>
      </Box>
      <Box sx={{ position: 'relative', height: 8, borderRadius: 4, bgcolor: sk.isTerminal ? sk.bg2 : alpha(sk.sub, 0.12), overflow: 'visible' }}>
        {/* center tick */}
        <Box sx={{ position: 'absolute', left: '50%', top: -2, bottom: -2, width: 2, bgcolor: sk.faint, borderRadius: 1, transform: 'translateX(-50%)' }} />
        {/* needle */}
        <Box sx={{
          position: 'absolute', top: '50%', left: `${pos}%`,
          transform: 'translate(-50%, -50%)',
          width: 16, height: 16, borderRadius: '50%',
          bgcolor: color, border: `3px solid ${sk.isTerminal ? sk.bg1 : '#fff'}`,
          boxShadow: `0 0 0 1px ${alpha(color, 0.4)}`,
          transition: 'left 0.35s ease',
        }} />
      </Box>
    </Box>
  );
}

function MetricCompareRow({ label, aVal, bVal, fmt, lowerWins = false }) {
  const sk = useSkin();
  const aN = aVal ?? 0;
  const bN = bVal ?? 0;
  const winner = aN === bN ? null : (lowerWins ? aN < bN : aN > bN) ? 'A' : 'B';
  const total = Math.abs(aN) + Math.abs(bN);
  const aShare = total > 0 ? (Math.abs(aN) / total) * 100 : 50;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.6 }}>
      <Typography variant="body2" sx={{ fontFamily: sk.mono, fontWeight: winner === 'A' ? 800 : 500, color: winner === 'A' ? sk.pos : sk.text, width: 84, textAlign: 'right', fontSize: '0.8rem' }}>
        {fmt(aVal)}
      </Typography>
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" sx={{ color: sk.faint, display: 'block', textAlign: 'center', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.25 }}>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', height: 4, borderRadius: 2, overflow: 'hidden', bgcolor: sk.isTerminal ? sk.bg2 : alpha(sk.sub, 0.1) }}>
          <Box sx={{ width: `${aShare}%`, bgcolor: winner === 'A' ? sk.pos : alpha(sk.sub, 0.5), transition: 'width 0.3s' }} />
          <Box sx={{ flex: 1, bgcolor: winner === 'B' ? sk.pos : alpha(sk.sub, 0.3), transition: 'width 0.3s' }} />
        </Box>
      </Box>
      <Typography variant="body2" sx={{ fontFamily: sk.mono, fontWeight: winner === 'B' ? 800 : 500, color: winner === 'B' ? sk.pos : sk.text, width: 84, fontSize: '0.8rem' }}>
        {fmt(bVal)}
      </Typography>
    </Box>
  );
}

function VerdictPanel({ sideA, sideB }) {
  const sk = useSkin();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const verdict = useMemo(
    () => (sideA.length && sideB.length ? computeVerdict(sideA, sideB) : null),
    [sideA, sideB]
  );

  if (!verdict) {
    return (
      <Box sx={{ ...sk.card, mt: 2, py: 3, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: sk.faint }}>
          Add players to both sides to get a verdict
        </Typography>
      </Box>
    );
  }

  const color = bandColor(verdict.band, sk);
  const absDiff = fmtDollar(Math.abs(verdict.diff));
  let headline;
  let sub;
  if (verdict.band === 'FAIR') {
    headline = 'Fair trade';
    sub = `Both packages land within ${absDiff} of each other in surplus value.`;
  } else {
    const w = verdict.winner === 'A' ? 'Side A' : 'Side B';
    headline = verdict.band === 'SLIGHT'
      ? `Slight edge — ${w}`
      : verdict.band === 'CLEAR'
        ? `${w} wins this deal`
        : `Lopsided toward ${w}`;
    sub = `${w}'s package carries ${absDiff} more surplus value — the team acquiring it comes out ahead.`;
  }

  const copySummary = () => {
    const names = (ps) => ps.map((p) => p.name).join(', ');
    const text = `${headline} · Side A (${names(sideA)}) ${fmtSurplus(verdict.a.surplus)} vs Side B (${names(sideB)}) ${fmtSurplus(verdict.b.surplus)} — thecycle.online/trades`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
      <Box sx={{ ...sk.card, mt: 2, px: { xs: 2, sm: 3 }, py: 2, borderTop: `3px solid ${color}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
          <Typography variant="h6" fontWeight={800} sx={{ color: sk.text, fontSize: { xs: '1rem', sm: '1.15rem' }, lineHeight: 1.2 }}>
            {headline}
          </Typography>
          <Chip
            label={BAND_META[verdict.band].label}
            size="small"
            sx={{ height: 18, fontSize: '0.6rem', fontWeight: 800, bgcolor: alpha(color, 0.15), color, textTransform: 'uppercase', letterSpacing: 0.5 }}
          />
          <Box sx={{ flex: 1 }} />
          <Tooltip title={copied ? 'Copied!' : 'Copy summary'} arrow>
            <IconButton size="small" onClick={copySummary} sx={{ color: copied ? sk.pos : sk.faint }}>
              <ContentCopy sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="body2" sx={{ color: sk.sub, mt: 0.5 }}>
          {sub}
        </Typography>

        <FairnessBar verdict={verdict} />

        <ButtonBase
          onClick={() => setOpen((o) => !o)}
          disableRipple
          sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5, color: sk.sub, '&:hover': { color: sk.text } }}
        >
          <Typography variant="caption" fontWeight={700}>Why?</Typography>
          <ExpandMore sx={{ fontSize: 16, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
        </ButtonBase>

        <Collapse in={open}>
          <Box sx={{ mt: 1, pt: 1, borderTop: `1px dashed ${sk.border}` }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" fontWeight={800} sx={{ color: sk.sub, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.62rem' }}>Side A</Typography>
              <Typography variant="caption" fontWeight={800} sx={{ color: sk.sub, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.62rem' }}>Side B</Typography>
            </Box>
            <MetricCompareRow label="Surplus value" aVal={verdict.a.surplus} bVal={verdict.b.surplus} fmt={fmtSurplus} />
            <MetricCompareRow label="Projected WAR" aVal={verdict.a.war} bVal={verdict.b.war} fmt={(v) => fmtStat(v)} />
            <MetricCompareRow label="Trade score (CTS)" aVal={verdict.a.cts} bVal={verdict.b.cts} fmt={(v) => fmtStat(v, 0)} />
            <MetricCompareRow label="Years of control" aVal={verdict.a.control} bVal={verdict.b.control} fmt={(v) => `${v} yr${v === 1 ? '' : 's'}`} />
            <MetricCompareRow label="Salary owed" aVal={verdict.a.salary} bVal={verdict.b.salary} fmt={fmtDollar} lowerWins />
            <Typography variant="caption" sx={{ color: sk.faint, display: 'block', mt: 1, fontSize: '0.66rem' }}>
              Verdict is based on total contract surplus — the market value of each player's projected production over
              his remaining team control, minus salary owed. Numbers match the player cards above.
            </Typography>
          </Box>
        </Collapse>
      </Box>
    </motion.div>
  );
}

// ─── War Room ────────────────────────────────────────────────────────────────
function WarRoom() {
  const sk = useSkin();
  const [sideA, setSideA] = useState([]);
  const [sideB, setSideB] = useState([]);

  const allIds = useMemo(
    () => [...sideA, ...sideB].map((p) => p.playerId),
    [sideA, sideB]
  );

  return (
    <Box>
      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={5.5}>
          <TradeSidePanel label="Side A" players={sideA} setPlayers={setSideA} excludeIds={allIds} />
        </Grid>
        <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pt: { xs: 0, md: 6 } }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(sk.accent, 0.1), border: `1.5px solid ${alpha(sk.accent, 0.3)}`,
          }}>
            <SwapHoriz sx={{ color: sk.accent, fontSize: 20 }} />
          </Box>
        </Grid>
        <Grid item xs={12} md={5.5}>
          <TradeSidePanel label="Side B" players={sideB} setPlayers={setSideB} excludeIds={allIds} />
        </Grid>
      </Grid>

      <VerdictPanel sideA={sideA} sideB={sideB} />
    </Box>
  );
}

// ─── Market ──────────────────────────────────────────────────────────────────
function Market() {
  const theme = useTheme();
  const sk = useSkin();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { players, loading } = useTradeData();

  const [category, setCategory] = useState('all');
  const [posFilter, setPosFilter] = useState(null);
  const [teamFilter, setTeamFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('tradeValue');
  const [sortDir, setSortDir] = useState('desc');

  const allTeams = useMemo(
    () => [...new Set(players.map((p) => p.team).filter(Boolean))].sort(),
    [players]
  );

  const filtered = useMemo(() => {
    let list = players;

    if (category === 'batting') list = list.filter((p) => !isPitcher(p));
    else if (category === 'pitching') list = list.filter((p) => isPitcher(p));

    if (teamFilter) list = list.filter((p) => p.team === teamFilter);

    if (posFilter) {
      list = list.filter((p) => {
        const role = playerRole(p);
        if (posFilter === 'RP') return role === 'RP' || role === 'CL';
        if (posFilter === 'SP') return role === 'SP';
        return role === posFilter;
      });
    }

    if (searchTerm.length >= 2) {
      const q = searchTerm.toLowerCase();
      list = list.filter((p) => (p.name || '').toLowerCase().includes(q));
    }

    return [...list].sort((a, b) => {
      let aV, bV;
      switch (sortField) {
        case 'name':
          return sortDir === 'asc'
            ? (a.name || '').localeCompare(b.name || '')
            : (b.name || '').localeCompare(a.name || '');
        case 'war': aV = playerWAR(a); bV = playerWAR(b); break;
        case 'age': aV = a.age || 0; bV = b.age || 0; break;
        case 'salary': aV = playerSalary(a) || 0; bV = playerSalary(b) || 0; break;
        case 'surplus':
          aV = playerSurplus(a) ?? -Infinity;
          bV = playerSurplus(b) ?? -Infinity;
          break;
        case 'cvr': aV = playerCVR(a) || 0; bV = playerCVR(b) || 0; break;
        default: aV = a.tradeValue || 0; bV = b.tradeValue || 0;
      }
      return sortDir === 'asc' ? aV - bV : bV - aV;
    });
  }, [players, category, teamFilter, posFilter, searchTerm, sortField, sortDir]);

  const handleSort = (field) => {
    if (field === sortField) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortField(field); setSortDir('desc'); }
  };

  const chipSx = (active) => ({
    fontWeight: active ? 700 : 500,
    cursor: 'pointer',
    fontSize: '0.72rem',
    height: 24,
    bgcolor: active
      ? (sk.isTerminal ? alpha(sk.accent, 0.18) : theme.palette.primary.main)
      : (sk.isTerminal ? sk.bg2 : alpha(theme.palette.text.primary, 0.06)),
    color: active
      ? (sk.isTerminal ? sk.accent : '#fff')
      : (sk.isTerminal ? sk.sub : theme.palette.text.primary),
    border: sk.isTerminal ? `1px solid ${active ? alpha(sk.accent, 0.5) : sk.border}` : 'none',
    '&:hover': { bgcolor: active ? undefined : sk.hoverBg },
  });

  const SortHdr = ({ field, children, align, sx: sxProp }) => (
    <TableCell
      align={align}
      onClick={() => handleSort(field)}
      sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', '&:hover': { color: sk.accent }, ...sxProp }}
    >
      {children}{sortField === field ? (sortDir === 'desc' ? ' ▼' : ' ▲') : ''}
    </TableCell>
  );

  if (loading) {
    return (
      <Stack spacing={1}>
        {[...Array(8)].map((_, i) => (
          <Skeleton key={i} variant="rounded" height={48} sx={{ borderRadius: 2 }} />
        ))}
      </Stack>
    );
  }

  const fieldSx = sk.isTerminal ? {
    '& .MuiOutlinedInput-root': {
      color: sk.text, fontSize: '0.82rem',
      '& fieldset': { borderColor: sk.border }, '&:hover fieldset': { borderColor: sk.faint },
      '&.Mui-focused fieldset': { borderColor: sk.accent },
    },
    '& input::placeholder': { color: sk.faint, opacity: 1 },
  } : {};

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ ...sk.card, mb: 2, p: 1.5 }}>
        <Grid container spacing={1.25} alignItems="center">
          <Grid item xs={12} sm="auto">
            <Stack direction="row" spacing={0.5}>
              {['all', 'batting', 'pitching'].map((c) => (
                <Chip key={c} label={c.charAt(0).toUpperCase() + c.slice(1)} size="small" clickable
                  onClick={() => { setCategory(c); setPosFilter(null); }} sx={chipSx(category === c)} />
              ))}
            </Stack>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              size="small" fullWidth placeholder="Search player…"
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 15, color: sk.faint }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={2.5}>
            <Autocomplete
              size="small"
              options={allTeams}
              value={teamFilter}
              onChange={(_, v) => setTeamFilter(v)}
              renderOption={(props, opt) => (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar src={getTeamLogoUrl(opt)} sx={{ width: 18, height: 18 }} imgProps={{ style: { objectFit: 'contain', background: 'white' } }} />
                  <Typography variant="body2">{opt}</Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} placeholder="All teams" size="small" sx={fieldSx} />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              <Chip label="All" size="small" clickable onClick={() => setPosFilter(null)} sx={chipSx(!posFilter)} />
              {MARKET_POSITIONS.map((pos) => (
                <Chip key={pos} label={pos} size="small" clickable
                  onClick={() => setPosFilter(posFilter === pos ? null : pos)}
                  sx={chipSx(posFilter === pos)} />
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Table */}
      <Box sx={{ ...sk.card, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 270px)' }}>
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: isMobile ? 430 : 860,
              '& .MuiTableCell-root': {
                borderColor: sk.border,
                ...(sk.isTerminal ? { color: sk.text, py: 0.5 } : {}),
              },
              '& .MuiTableCell-head': {
                fontWeight: 700,
                ...(sk.isTerminal ? {
                  bgcolor: sk.bg2, color: sk.sub,
                  fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: 0.6,
                } : {}),
              },
              ...(sk.isTerminal ? { '& .MuiTableRow-hover:hover': { bgcolor: sk.hoverBg + ' !important' } } : {}),
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40, color: sk.faint, display: { xs: 'none', sm: 'table-cell' } }}>#</TableCell>
                <SortHdr field="name">Player</SortHdr>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Tier</TableCell>
                <SortHdr field="surplus" align="right">
                  <Tooltip title="Contract surplus — market value of projected WAR over remaining control, minus salary owed" arrow placement="top">
                    <span style={{ borderBottom: '1px dashed currentColor', cursor: 'help' }}>Surplus</span>
                  </Tooltip>
                </SortHdr>
                <SortHdr field="tradeValue" align="right">
                  <Tooltip title="CVR Trade Score — 0–100 composite value. Negative = albatross contract." arrow placement="top">
                    <span style={{ borderBottom: '1px dashed currentColor', cursor: 'help' }}>CTS</span>
                  </Tooltip>
                </SortHdr>
                <SortHdr field="war" align="right">WAR</SortHdr>
                <SortHdr field="cvr" align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>CVR</SortHdr>
                <SortHdr field="age" align="center">Age</SortHdr>
                <SortHdr field="salary" align="right">Salary</SortHdr>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((p, idx) => {
                const tv = p.tradeValue || 0;
                const war = playerWAR(p);
                const cvr = playerCVR(p);
                const surplus = playerSurplus(p);
                const tier = tierFor(p);
                return (
                  <TableRow key={p.playerId} hover
                    sx={{ ...(tv < 0 ? { bgcolor: alpha(sk.neg, 0.04) } : {}) }}>
                    <TableCell sx={{ color: sk.faint, fontSize: '0.75rem', display: { xs: 'none', sm: 'table-cell' } }}>
                      {idx + 1}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <PlayerFace player={p} size={30} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap sx={{ color: sk.text, fontSize: '0.82rem' }}>{p.name}</Typography>
                          <Typography variant="caption" sx={{ color: sk.sub, fontSize: '0.68rem' }}>{p.team} · {playerRole(p)}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Chip label={tier.label} size="small"
                        sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha(tier.color, 0.13), color: tier.color }} />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontFamily: sk.mono, fontWeight: 700, fontSize: '0.8rem', color: surplusColor(surplus, sk) }}>
                        {fmtSurplus(surplus)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={700} sx={{ fontFamily: sk.mono, fontSize: '0.8rem', color: tvColor(tv, sk) }}>
                        {tv.toFixed(1)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontFamily: sk.mono, fontSize: '0.8rem', color: war >= 2 ? sk.pos : war < 0 ? sk.neg : sk.text }}>
                        {fmtStat(war)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Typography variant="body2" sx={{ fontFamily: sk.mono, fontSize: '0.8rem', color: sk.sub }}>
                        {fmtStat(cvr)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontFamily: sk.mono, fontSize: '0.8rem', color: sk.text }}>{p.age || '—'}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontFamily: sk.mono, fontSize: '0.78rem', color: sk.text }}>
                        {fmtDollar(playerSalary(p))}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{
          px: 2, py: 1, borderTop: `1px solid ${sk.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <Typography variant="caption" sx={{ color: sk.sub }}>
            {filtered.length} of {players.length} players
            {teamFilter ? ' · ' + teamFilter : ''}
            {posFilter ? ' · ' + posFilter : ''}
          </Typography>
          <Chip label="Rolling 30-day" size="small" variant="outlined"
            sx={{ height: 20, fontSize: '0.62rem', color: sk.accent, borderColor: alpha(sk.accent, 0.5) }} />
        </Box>
      </Box>
    </Box>
  );
}

// ─── Compare ─────────────────────────────────────────────────────────────────
function PlayerSelectorCard({ label, player, onSelect, onClear, excludeIds, accent }) {
  const sk = useSkin();
  const tv = player?.tradeValue || 0;
  const tier = player ? tierFor(player) : null;
  return (
    <Box sx={{ ...sk.card, minHeight: 106, borderColor: alpha(accent, 0.35) }}>
      <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${sk.border}` }}>
        <Typography variant="subtitle2" fontWeight={800} sx={{ color: sk.text, fontSize: '0.78rem' }}>{label}</Typography>
      </Box>
      <Box sx={{ p: 1.5 }}>
        {!player ? (
          <PlayerSearch onSelect={onSelect} excludeIds={excludeIds} placeholder="Search a player…" />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PlayerFace player={player} size={44} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700} noWrap sx={{ color: sk.text }}>{player.name}</Typography>
              <Typography variant="caption" sx={{ color: sk.sub, display: 'block' }}>
                {player.team} · {playerRole(player)} · Age {player.age || '?'}
              </Typography>
              {tier && (
                <Chip label={tier.label} size="small"
                  sx={{ mt: 0.25, height: 15, fontSize: '0.58rem', fontWeight: 700, bgcolor: alpha(tier.color, 0.13), color: tier.color, '& .MuiChip-label': { px: 0.6 } }} />
              )}
            </Box>
            <Chip label={fmtStat(tv)}
              sx={{ fontFamily: sk.mono, fontWeight: 700, bgcolor: alpha(tvColor(tv, sk), 0.12), color: tvColor(tv, sk), flexShrink: 0 }} />
            <IconButton size="small" onClick={onClear} sx={{ color: sk.faint }}>
              <Close sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function CompareResult({ result, playerA, playerB }) {
  const sk = useSkin();
  const colorA = sk.accent;
  const colorB = sk.info;

  const bdA = result?.playerA?.breakdown || {};
  const bdB = result?.playerB?.breakdown || {};

  const radarData = [
    { axis: 'CVR',        A: bdA.cvrProduction || 0, B: bdB.cvrProduction || 0 },
    { axis: 'Efficiency', A: bdA.acvrEfficiency || 0, B: bdB.acvrEfficiency || 0 },
    { axis: 'Scarcity',   A: bdA.scarcity || 0, B: bdB.scarcity || 0 },
    { axis: 'Age',        A: bdA.ageTrajectory || 0, B: bdB.ageTrajectory || 0 },
    { axis: 'Control',    A: bdA.teamControl || 0, B: bdB.teamControl || 0 },
  ];
  const hasRadar = radarData.some((r) => r.A > 0 || r.B > 0);

  const tvA = result?.playerA?.tradeValue ?? playerA?.tradeValue ?? 0;
  const tvB = result?.playerB?.tradeValue ?? playerB?.tradeValue ?? 0;
  const warA = playerWAR(playerA);
  const warB = playerWAR(playerB);
  const surpA = result?.playerA?.surplusValue ?? playerSurplus(playerA);
  const surpB = result?.playerB?.surplusValue ?? playerSurplus(playerB);
  const ctrlA = playerControl(result?.playerA) ?? playerControl(playerA);
  const ctrlB = playerControl(result?.playerB) ?? playerControl(playerB);

  const metrics = [
    { label: 'Surplus', a: fmtSurplus(surpA), b: fmtSurplus(surpB), better: surpA != null && surpB != null ? (surpA > surpB ? 'A' : surpB > surpA ? 'B' : null) : null },
    { label: 'WAR', a: fmtStat(warA), b: fmtStat(warB), better: warA > warB ? 'A' : warB > warA ? 'B' : null },
    { label: 'Control', a: ctrlA != null ? ctrlA + ' yrs' : '—', b: ctrlB != null ? ctrlB + ' yrs' : '—', better: ctrlA != null && ctrlB != null ? (ctrlA > ctrlB ? 'A' : ctrlB > ctrlA ? 'B' : null) : null },
    { label: 'CVR', a: fmtStat(playerCVR(playerA)), b: fmtStat(playerCVR(playerB)), better: null },
    { label: 'CTS', a: fmtStat(tvA), b: fmtStat(tvB), better: tvA > tvB ? 'A' : tvB > tvA ? 'B' : null },
    { label: 'Age', a: playerA?.age || '—', b: playerB?.age || '—', better: playerA?.age && playerB?.age ? (playerA.age < playerB.age ? 'A' : 'B') : null },
    { label: 'Salary', a: fmtDollar(playerSalary(playerA)), b: fmtDollar(playerSalary(playerB)), better: null },
  ];

  const nameA = playerA?.name?.split(' ').pop() || 'A';
  const nameB = playerB?.name?.split(' ').pop() || 'B';

  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={hasRadar ? 5 : 12}>
          <Box sx={{ ...sk.card, overflow: 'hidden' }}>
            <Table size="small" sx={{
              '& .MuiTableCell-root': { borderColor: sk.border, ...(sk.isTerminal ? { color: sk.text, py: 0.6 } : {}) },
            }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: sk.sub }}>Metric</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: colorA + ' !important' }}>{nameA}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: colorB + ' !important' }}>{nameB}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {metrics.map((m) => (
                  <TableRow key={m.label}>
                    <TableCell sx={{ color: sk.sub, fontWeight: 600 }}>{m.label}</TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontFamily: sk.mono, fontWeight: m.better === 'A' ? 800 : 500, color: m.better === 'A' ? sk.pos : sk.text }}>
                        {m.a}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontFamily: sk.mono, fontWeight: m.better === 'B' ? 800 : 500, color: m.better === 'B' ? sk.pos : sk.text }}>
                        {m.b}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Grid>

        {hasRadar && (
          <Grid item xs={12} md={7}>
            <Box sx={{ ...sk.card, p: 2, height: '100%' }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: sk.text }}>CTS Profile</Typography>
              <Box sx={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData}>
                    <PolarGrid stroke={sk.isTerminal ? 'rgba(255,255,255,0.08)' : alpha(sk.sub, 0.2)} />
                    <PolarAngleAxis dataKey="axis" tick={{ fill: sk.sub, fontSize: 12 }} />
                    <PolarRadiusAxis tick={false} axisLine={false} domain={[0, 100]} />
                    <Radar name={nameA} dataKey="A" stroke={colorA} fill={alpha(colorA, 0.15)} strokeWidth={2} />
                    <Radar name={nameB} dataKey="B" stroke={colorB} fill={alpha(colorB, 0.15)} strokeWidth={2} />
                    <Legend wrapperStyle={{ fontSize: 12, color: sk.sub }} iconType="circle" iconSize={8} />
                  </RadarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>
    </motion.div>
  );
}

function Compare() {
  const sk = useSkin();
  const [playerA, setPlayerA] = useState(null);
  const [playerB, setPlayerB] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!playerA || !playerB) { setComparison(null); return; }
    setLoading(true);
    tradeApi
      .compareTradeValues(playerA.playerId, playerB.playerId, null, { mode: 'rolling' })
      .then(setComparison)
      .catch(() => setComparison(null))
      .finally(() => setLoading(false));
  }, [playerA, playerB]);

  const excludeForA = useMemo(() => (playerB ? [playerB.playerId] : []), [playerB]);
  const excludeForB = useMemo(() => (playerA ? [playerA.playerId] : []), [playerA]);

  return (
    <Box>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={5}>
          <PlayerSelectorCard
            label="Player A" player={playerA} onSelect={setPlayerA}
            onClear={() => { setPlayerA(null); setComparison(null); }}
            excludeIds={excludeForA} accent={sk.accent}
          />
        </Grid>
        <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pt: { xs: 0, md: 4.5 } }}>
          <Box sx={{
            width: 38, height: 38, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(sk.accent, 0.08), border: `1.5px solid ${alpha(sk.accent, 0.25)}`,
          }}>
            <CompareArrows sx={{ color: sk.accent, fontSize: 19 }} />
          </Box>
        </Grid>
        <Grid item xs={12} md={5}>
          <PlayerSelectorCard
            label="Player B" player={playerB} onSelect={setPlayerB}
            onClear={() => { setPlayerB(null); setComparison(null); }}
            excludeIds={excludeForB} accent={sk.info}
          />
        </Grid>
      </Grid>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size={28} /></Box>}
      {comparison && !loading && <CompareResult result={comparison} playerA={playerA} playerB={playerB} />}
    </Box>
  );
}

// ─── Main ────────────────────────────────────────────────────────────────────
const TAB_KEYS = ['war-room', 'market', 'compare'];
const SKIN_STORAGE_KEY = 'tradeRoomSkin';

export default function Trades() {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = Math.max(0, TAB_KEYS.indexOf(searchParams.get('tab') || ''));
  const [tab, setTab] = useState(initialTab);
  const [skinKey, setSkinKey] = useState(() => {
    try {
      const saved = localStorage.getItem(SKIN_STORAGE_KEY);
      return saved === 'terminal' ? 'terminal' : 'simulator';
    } catch { return 'simulator'; }
  });

  const sk = useMemo(() => makeSkin(theme, skinKey), [theme, skinKey]);

  const handleTabChange = (_, v) => {
    setTab(v);
    setSearchParams({ tab: TAB_KEYS[v] || 'war-room' }, { replace: true });
  };

  const handleSkinChange = useCallback((_, v) => {
    if (!v) return;
    setSkinKey(v);
    try { localStorage.setItem(SKIN_STORAGE_KEY, v); } catch { /* ignore */ }
  }, []);

  return (
    <TradeDataProvider>
      <SkinContext.Provider value={sk}>
        <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1400, mx: 'auto', width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
          <Box sx={{
            ...(sk.isTerminal ? {
              bgcolor: sk.bg0, borderRadius: 2, p: { xs: 1.5, sm: 2.5 },
              border: `1px solid ${sk.border}`,
            } : {}),
          }}>
            {/* Title row */}
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, mb: 1.5 }}>
              <Typography variant="h6" fontWeight={800} sx={{ color: sk.text, lineHeight: 1.2 }}>
                Trade Room
              </Typography>
              <Typography variant="caption" sx={{ color: sk.faint }}>
                Rolling 30-day valuations
              </Typography>
            </Box>

            {/* Control row — tabs + skin toggle (mirrors /players styling) */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2,
              borderBottom: `1px solid ${sk.border}`, pb: 0.5,
            }}>
              <Tabs
                value={tab}
                onChange={handleTabChange}
                sx={{
                  minHeight: 36,
                  '& .MuiTab-root': {
                    minHeight: 36, textTransform: 'none', fontWeight: 600,
                    fontSize: '0.8rem', px: 1.5, minWidth: 'auto',
                    color: sk.isTerminal ? sk.sub : undefined,
                    '&.Mui-selected': { color: sk.accent },
                  },
                  '& .MuiTabs-indicator': { height: 2.5, borderRadius: '2px 2px 0 0', bgcolor: sk.accent },
                }}
              >
                <Tab label="War Room" />
                <Tab label="Market" />
                <Tab label="Compare" />
              </Tabs>

              <Box sx={{ flex: 1 }} />

              <Divider orientation="vertical" flexItem sx={{ my: 0.75, borderColor: sk.border, display: { xs: 'none', sm: 'block' } }} />

              <ToggleButtonGroup
                value={skinKey}
                exclusive
                onChange={handleSkinChange}
                size="small"
                sx={{
                  bgcolor: sk.isTerminal ? sk.bg2 : alpha(theme.palette.text.primary, 0.04),
                  borderRadius: '9px', p: 0.35, border: 'none',
                }}
              >
                {[['simulator', 'Simulator'], ['terminal', 'Terminal']].map(([key, label]) => (
                  <ToggleButton
                    key={key} value={key} disableRipple
                    sx={{
                      textTransform: 'none', fontWeight: skinKey === key ? 700 : 500,
                      fontSize: '0.72rem', px: 1.25, py: 0.25,
                      border: 'none !important', borderRadius: '6px !important',
                      color: skinKey === key ? sk.accent : sk.sub,
                      bgcolor: skinKey === key ? alpha(sk.accent, 0.12) + ' !important' : 'transparent !important',
                      transition: 'all 0.15s',
                    }}
                  >
                    {label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {/* Tab content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={`${tab}-${skinKey}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.16 }}
              >
                {tab === 0 && <WarRoom />}
                {tab === 1 && <Market />}
                {tab === 2 && <Compare />}
              </motion.div>
            </AnimatePresence>
          </Box>
        </Box>
      </SkinContext.Provider>
    </TradeDataProvider>
  );
}
